/** Paid tier value written by the Stripe webhook. Legacy rows may still say 'paid' — treat both as
 *  paid via isPaidTier(). Canonical going forward is 'pro' (matches backend + /pricing). */
export type Tier = 'free' | 'pro' | 'paid';

/** True for any non-free tier — tolerates the historical 'paid'/'pro' split. */
export const isPaidTier = (tier: Tier): boolean => tier !== 'free';

/** User profile from GET /users/me */
export interface UserProfile {
  userId: string;
  tier: Tier;
  subscriptionStatus?: string;
  stripeCustomerId?: string | null;
  gamesAnalyzedThisMonth: number;
  coachCallsThisMonth: number;
  currentMonthKey: string;
  preferences: UserPreferences;
  chessUsername?: string;
  chessPlatform?: string;
  /** No coaching ceiling: set by hand on an account Sam picks (his own, an invitee's); llm_stream skips the limit. */
  unmetered?: boolean;
  /** In-flight Pro backfill job, if any — drives the progress banner. */
  activeBackfillJobId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserPreferences {
  defaultDepth?: number;
  autoFlipBoard?: boolean;
  showEvalBar?: boolean;
  coachLevel?: 'beginner' | 'advanced';
}

export interface UpdateUserRequest {
  preferences?: Partial<UserPreferences>;
  chessUsername?: string;
  chessPlatform?: string;
  coachLevel?: 'beginner' | 'advanced';
}

/** Tier limits */
// Mirrors backend FREE_MONTHLY_COACH_LIMIT / PRO_MONTHLY_COACH_LIMIT and the /pricing page.
export const TIER_LIMITS = {
  free: { coachCallsPerMonth: 5, label: 'Free' },
  pro: { coachCallsPerMonth: 50, label: 'Pro' },
  paid: { coachCallsPerMonth: 50, label: 'Pro' },
} as const;

/** The account's monthly coached-review ceiling: its tier's, or none (Infinity) on an account set `unmetered` by hand
 *  (llm_stream skips its meter). The one place the plan button, the Coach tab and the Profile card read it from. */
export const coachLimit = (profile: Pick<UserProfile, 'tier' | 'unmetered'>): number =>
  profile.unmetered === true ? Infinity : (TIER_LIMITS[profile.tier] ?? TIER_LIMITS.free).coachCallsPerMonth;